/**
 * @file WebSocketContext.js
 * @brief Context de React para conexión WebSocket en tiempo real
 */

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket debe usarse dentro de WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [ws, setWs] = useState(null);
  const [connected, setConnected] = useState(false);
  const [sensorData, setSensorData] = useState({});
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;
    
    const websocket = new WebSocket(wsUrl);

    websocket.onopen = () => {
      console.log('WebSocket conectado');
      setConnected(true);
    };

    websocket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        
        switch (message.type) {
          case 'sensor_update':
            setSensorData(prev => ({
              ...prev,
              [message.data.bandeja_id]: message.data
            }));
            break;
            
          case 'alert':
            setAlerts(prev => [message.data, ...prev]);
            break;
            
          case 'status_update':
            // Actualizar estado de actuadores
            break;
            
          default:
            console.log('Mensaje WebSocket desconocido:', message);
        }
      } catch (error) {
        console.error('Error procesando mensaje WebSocket:', error);
      }
    };

    websocket.onerror = (error) => {
      console.error('Error WebSocket:', error);
      setConnected(false);
    };

    websocket.onclose = () => {
      console.log('WebSocket desconectado');
      setConnected(false);
    };

    setWs(websocket);

    return () => {
      websocket.close();
    };
  }, [isAuthenticated]);

  const sendMessage = useCallback((message) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket no está conectado');
    }
  }, [ws]);

  const value = {
    connected,
    sensorData,
    alerts,
    sendMessage
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};
